import { Gwei } from './gwei';
import { divide } from './math';
import { calcMarginRate, CalcMaxPositionParams, calcTotalCostPerUnit } from './maxPositionSize';

/**
 * Checks if the limit order's price is worse than the oracle price,
 * resulting in an immediate fill at an unfavorable price.
 */
function checkIfInstantLoss({
  side,
  oraclePrice,
  limitPrice,
}: {
  side: 'long' | 'short';
  oraclePrice: bigint;
  limitPrice: bigint;
}) {
  return (
    (side === 'long' && limitPrice > oraclePrice) || (side === 'short' && limitPrice < oraclePrice)
  );
}

/**
 * Calculates the total cost per unit in a scenario where the fill happens
 * immediately at a worse price, resulting in an "instant loss."
 */
function calcCostPerUnitImmediateLoss(
  {
    side,
    oraclePrice,
    limitPrice,
  }: {
    side: 'long' | 'short';
    oraclePrice: bigint;
    limitPrice: bigint;
  },
  marginRate: bigint
): bigint {
  // The execution price is whichever is worse for the trader:
  //  - for longs, the lower price is beneficial, so the "worse" immediate fill uses min(...)
  //  - for shorts, the higher price is beneficial, so the "worse" immediate fill uses max(...)
  const price =
    side === 'long' ? Gwei.min(oraclePrice, limitPrice) : Gwei.max(oraclePrice, limitPrice);

  // The immediate loss is the difference between limitPrice and oraclePrice in an unfavorable direction
  let priceDifference = side === 'long' ? limitPrice - oraclePrice : oraclePrice - limitPrice;
  if (priceDifference < 0n) {
    priceDifference = 0n;
  }

  // Total cost = margin + fees on `price` + immediate loss
  return calcTotalCostPerUnit({
    markPrice: price,
    marginRate,
    priceDifference,
  });
}

/**
 * Calculates the total cost per unit in a scenario where the limit price
 * does not create an immediate unfavorable fill (i.e. it's neutral or favorable).
 */
function calcCostPerUnitFavorable(
  { oraclePrice, limitPrice }: { oraclePrice: bigint; limitPrice: bigint },
  marginRate: bigint
): bigint {
  // Use the higher price to ensure we set aside enough margin, as the backend will use this logic for "fair price"
  // It's a little counterintuitive that the same price is used for both long and short,
  // the reason for this is that the reserved margin is based on the notional value of the position, and we want the highest possible value.
  const price = Gwei.max(oraclePrice, limitPrice);

  // No immediate loss in this scenario
  return calcTotalCostPerUnit({
    markPrice: price,
    marginRate,
  });
}

/**
 * Calculates maximum position size for limit orders.
 * Handles both favorable and unfavorable (instant loss) scenarios.
 */
export function calcMaxSizeWithLimitPrice(params: CalcMaxPositionParams) {
  const { availableMargin, oraclePrice, limitPrice, maxQuantity, side } = params;
  if (!availableMargin || !oraclePrice || !limitPrice) {
    return undefined;
  }

  // Determine if limit price is worse than the oracle price, causing an immediate fill at a loss
  const isInstantLoss = checkIfInstantLoss({ limitPrice, oraclePrice, side });

  // Compute marginRate from margin ratio + round-trip fees
  const initialMarginRatioBigInt = Gwei.toGwei(params.initialMarginRatio);
  const marginRate = calcMarginRate(initialMarginRatioBigInt, params.feeRate);

  // Calculate cost per unit differently based on immediate loss vs. favorable scenario
  const costPerUnit = isInstantLoss
    ? calcCostPerUnitImmediateLoss({ oraclePrice, limitPrice, side }, marginRate)
    : calcCostPerUnitFavorable({ oraclePrice, limitPrice }, marginRate);

  // Avoid division by zero if costPerUnit is 0
  if (costPerUnit === 0n) {
    return 0n;
  }

  // Calculate how much size we can afford
  const maxSizeFromMargin = divide(availableMargin, costPerUnit);

  // The the products max quantity into account too
  return Gwei.min(maxSizeFromMargin, maxQuantity);
}
